class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce(this.onSubmitHandler.bind(this), 500);

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('input', this.debouncedOnSubmit);
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state?.searchParams ?? FacetFiltersForm.searchParamsInitial;
      if (searchParams !== FacetFiltersForm.searchParamsPrev) {
        FacetFiltersForm.renderPage(searchParams, null, false);
      }
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const gridContainer = document.getElementById('ProductGridContainer');
    
    gridContainer?.querySelector('.collection')?.classList.add('loading');
    countContainer?.classList.add('loading');
    countContainerDesktop?.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const cachedData = FacetFiltersForm.filterData.find(item => item.url === url);

      if (cachedData) {
        FacetFiltersForm.renderSectionFromCache(cachedData, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static async renderSectionFromFetch(url, event) {
    try {
      const response = await fetch(url);
      const html = await response.text();
      
      FacetFiltersForm.filterData.push({ html, url });
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
    } catch (error) {
      console.error('Error fetching facets:', error);
    }
  }

  static renderSectionFromCache(cachedData, event) {
    const { html } = cachedData;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newContainer = doc.getElementById('ProductGridContainer');
    const currentContainer = document.getElementById('ProductGridContainer');
    
    if (newContainer && currentContainer) {
      currentContainer.innerHTML = newContainer.innerHTML;
    }
  }

  static renderProductCount(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const count = doc.getElementById('ProductCount')?.innerHTML;
    
    if (!count) return;
    
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(html, 'text/html');
    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const jsFilter = event?.target?.closest('.js-filter');
    
    const matchesIndex = (element) => jsFilter && element.dataset.index === jsFilter.dataset.index;
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) {
        target.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && jsFilter) {
      FacetFiltersForm.renderCounts(countsToRender, jsFilter);
    }
  }

  static renderActiveFacets(html) {
    const selectors = ['.active-facets-mobile', '.active-facets-desktop'];

    selectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      
      if (source && target) {
        target.innerHTML = source.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const selectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    selectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      
      if (source && target) {
        target.innerHTML = source.innerHTML;
      }
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    mobileForm?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const sourceElement = source.querySelector('.facets__selected');
    const targetElement = target.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const url = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    history.pushState({ searchParams }, '', url);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    return productGrid ? [{ section: productGrid.dataset.id }] : [];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    if (!form) return;
    
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    
    const href = event.currentTarget.href;
    const questionMarkIndex = href.indexOf('?');
    const url = questionMarkIndex === -1 ? '' : href.slice(questionMarkIndex + 1);
    
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.inputs = this.querySelectorAll('input');
    this.inputs.forEach(element => {
      element.addEventListener('change', this.onRangeChange.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.inputs;
    if (!minInput || !maxInput) return;
    
    const maxValue = maxInput.value;
    const minValue = minInput.value;
    
    minInput.setAttribute('max', maxValue || minInput.getAttribute('max'));
    maxInput.setAttribute('min', minValue || '0');
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) {
      input.value = min;
    } else if (value > max) {
      input.value = max;
    }
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector('a');
    if (link) {
      link.addEventListener('click', this.handleClick.bind(this));
    }
  }

  handleClick(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) {
      form.onActiveFilterClick(event);
    }
  }
}

customElements.define('facet-remove', FacetRemove);

// Mobile sort button handler
const initMobileSortButton = () => {
  const sortButton = document.querySelector('.facet-filters__field.filtering-btn');
  const sortSelect = document.getElementById('SortBy-mobile-btn');
  
  if (!sortButton || !sortSelect) return;
  
  sortButton.addEventListener('click', (e) => {
    if (e.target.closest('select')) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    sortSelect.focus();
    sortSelect.showPicker?.() ?? sortSelect.click();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileSortButton);
} else {
  initMobileSortButton();
}
